import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { deleteSync, getGantt, getKanban, getLineOfBalance, getProject, getSetupStatus, listSyncs, patchSync } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText, Trash2, Star, BarChart3, KanbanSquare, TrendingUp, AlertCircle, AlertTriangle, Settings, Sparkles, Cloud, GitCompare, RefreshCw, ExternalLink, Pencil } from "lucide-react";

import FileUploader from "../components/FileUploader";
import GanttView from "../components/GanttView";
import KanbanBoard from "../components/KanbanBoard";
import LineOfBalance from "../components/LineOfBalance";
import RestrictionsTable from "../components/RestrictionsTable";
import SyncComparator from "../components/SyncComparator";
import AIAnalysisPanel from "../components/AIAnalysisPanel";
import SharePointTab from "../components/sharepoint/SharePointTab";
import { reparseSync, reparseEmptySyncs, getIncompleteSyncsStats } from "../lib/api";

const TABS = [
  { key: "overview", labelKey: "overview", icon: BarChart3 },
  { key: "syncs", labelKey: "syncs", icon: FileText },
  { key: "gantt", labelKey: "gantt", icon: BarChart3 },
  { key: "kanban", labelKey: "kanban", icon: KanbanSquare },
  { key: "lob", labelKey: "line_of_balance", icon: TrendingUp },
  { key: "compare", labelKey: "sync_comparator", icon: GitCompare },
  { key: "restrictions", labelKey: "restrictions", icon: AlertCircle },
  { key: "sharepoint", labelKey: "sharepoint", icon: Cloud },
  { key: "settings", labelKey: "project_settings", icon: Settings },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "overview";

  const [project, setProject] = useState(null);
  const [setup, setSetup] = useState(null);
  const [syncs, setSyncs] = useState([]);
  const [incompleteStats, setIncompleteStats] = useState(null);
  const [activeSyncId, setActiveSyncId] = useState(null);
  const [loadingMain, setLoadingMain] = useState(true);

  const [tab, setTab] = useState(tabFromUrl);
  const [aiOpen, setAiOpen] = useState(false);

  // tab-specific data
  const [ganttData, setGanttData] = useState(null);
  const [kanbanData, setKanbanData] = useState(null);
  const [lobData, setLobData] = useState(null);
  const [criticalOnly, setCriticalOnly] = useState(false);

  useEffect(() => { setTab(tabFromUrl); }, [tabFromUrl]);
  useEffect(() => {
    // `setSearchParams` is stable per react-router-dom guarantee but eslint
    // still wants it listed; we intentionally only re-sync the URL when the
    // local `tab` state changes — not when other deps move.
    setSearchParams({ tab });
  }, [tab, setSearchParams]);

  const refreshProject = useCallback(async () => {
    setLoadingMain(true);
    try {
      const [p, s, st] = await Promise.all([getProject(id), listSyncs(id), getSetupStatus(id)]);
      setProject(p); setSetup(st);
      setSyncs(s.syncs || []);
      if (!activeSyncId && s.syncs?.length) setActiveSyncId(s.syncs[0].id);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setLoadingMain(false); }
    // Best-effort: incomplete-syncs detector. Admin-only endpoint — non-admins
    // get a 403 we swallow silently (the banner is admin-only anyway).
    if (isAdmin) {
      try {
        const stats = await getIncompleteSyncsStats(id);
        setIncompleteStats(stats);
      } catch {
        setIncompleteStats(null);
      }
    }
  }, [id, activeSyncId, isAdmin]);

  useEffect(() => { refreshProject(); }, [refreshProject]);

  // Load tab-specific data
  useEffect(() => {
    if (!activeSyncId) return;
    if (tab === "gantt") getGantt(activeSyncId).then(setGanttData).catch(() => setGanttData(null));
    if (tab === "kanban") getKanban(activeSyncId).then(setKanbanData).catch(() => setKanbanData(null));
    if (tab === "lob") getLineOfBalance(activeSyncId).then(setLobData).catch(() => setLobData(null));
  }, [tab, activeSyncId]);

  const activeSync = syncs.find((s) => s.id === activeSyncId);

  // Memoised props for <GanttView> — these are stable object literals that
  // would otherwise re-allocate on every render and bust the chart's
  // memoization (causing the virtualised list to recompute).
  const ganttSelection = useMemo(
    () => (activeSyncId ? [{ project_id: id, sync_id: activeSyncId }] : []),
    [id, activeSyncId],
  );
  const ganttSyncMeta = useMemo(
    () => (activeSyncId ? { [activeSyncId]: { name: activeSync?.name, version: activeSync?.version, color: project?.color } } : {}),
    [activeSyncId, activeSync?.name, activeSync?.version, project?.color],
  );
  const ganttProjects = useMemo(
    () => (project ? { [id]: { id, name: project.name, color: project.color } } : {}),
    [id, project],
  );
  const ganttInitialTasks = useMemo(
    () => (ganttData?.tasks || []).map((tk) => ({ ...tk, project_id: id, sync_id: activeSyncId })),
    [ganttData, id, activeSyncId],
  );

  const onDeleteSync = async (sid) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteSync(sid); toast.success(t("success")); await refreshProject(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onSetBaseline = async (sid) => {
    try { await patchSync(sid, { is_baseline: true }); toast.success(t("success")); await refreshProject(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onReparse = async (sid) => {
    try {
      const r = await reparseSync(sid);
      if (r.task_count > 0) toast.success(`${r.task_count} tarefas reprocessadas`);
      else if (r.warnings?.length) toast.warning(r.warnings[0]);
      else toast.info("Reprocessamento concluído — nenhuma tarefa encontrada.");
      await refreshProject();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onReparseAllEmpty = async () => {
    try {
      const r = await reparseEmptySyncs(id);
      if (r.total_succeeded > 0) toast.success(`${r.total_succeeded}/${r.total_processed} versões reprocessadas`);
      else toast.info("Nenhuma versão vazia para reprocessar.");
      await refreshProject();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onRenameSync = async (sid, newName) => {
    try {
      await patchSync(sid, { name: newName });
      toast.success(t("success"));
      await refreshProject();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  if (loadingMain || !project) {
    return <div className="px-12 py-16 text-sm text-zinc-500" data-testid="project-loading">{t("loading")}</div>;
  }

  return (
    <div className="flex flex-col min-h-screen" data-testid="project-detail">
      {/* Top header */}
      <div className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-8 lg:px-12 pt-6 pb-3 max-w-7xl">
          <button onClick={() => navigate("/projects")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="project-back-btn">
            <ArrowLeft size={12} /> {t("projects_title")}
          </button>
          <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: project.color || "#F97316" }} />
                <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 truncate" data-testid="project-name">{project.name}</h1>
              </div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                {project.client_name && <span>{project.client_name} · </span>}
                {project.location && <span>{project.location} · </span>}
                <span className="uppercase">{project.status}</span>
              </div>
            </div>
            {activeSyncId && (
              <button onClick={() => setAiOpen(true)} className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-3.5 py-2 rounded-md text-sm font-semibold hover:bg-zinc-800" data-testid="project-ai-btn">
                <Sparkles size={14} className="text-cs-accent" /> {t("ai_analysis")}
              </button>
            )}
          </div>
        </div>

        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl overflow-x-auto scrollbar-thin">
          <div className="flex gap-1 -mb-px">
            {TABS.map((tt) => (
              <button key={tt.key} onClick={() => setTab(tt.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap shrink-0 ${tab === tt.key ? "border-cs-accent text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
                data-testid={`project-tab-${tt.key}`}
              >
                <tt.icon size={13} />
                {t(tt.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sync selector (shown on most tabs) */}
      {(tab === "kanban" || tab === "lob") && syncs.length > 0 && (
        <div className="bg-zinc-50 border-b border-zinc-200 px-8 lg:px-12 py-3 max-w-7xl">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="cs-label">{t("version")}:</div>
            <select value={activeSyncId || ""} onChange={(e) => setActiveSyncId(e.target.value)}
              className="px-3 py-1.5 border border-zinc-300 rounded-md text-xs font-mono" data-testid="active-sync-select"
            >
              {syncs.map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}{s.is_baseline ? " (baseline)" : ""}</option>)}
            </select>
            {activeSync && <span className="font-mono text-xs text-zinc-500">{activeSync.task_count} {t("tasks")}</span>}
          </div>
        </div>
      )}
      {tab === "gantt" && syncs.length > 0 && (
        <div className="bg-zinc-50 border-b border-zinc-200 px-8 lg:px-12 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="cs-label">{t("version")}:</div>
            <select value={activeSyncId || ""} onChange={(e) => setActiveSyncId(e.target.value)}
              className="px-3 py-1.5 border border-zinc-300 rounded-md text-xs font-mono" data-testid="active-sync-select"
            >
              {syncs.map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}{s.is_baseline ? " (baseline)" : ""}</option>)}
            </select>
            {activeSync && <span className="font-mono text-xs text-zinc-500">{activeSync.task_count} {t("tasks")}</span>}
          </div>
        </div>
      )}

      <div className={`${tab === "gantt" ? "px-2 sm:px-4 lg:px-6 py-4 w-full" : "px-4 sm:px-8 lg:px-12 py-6 max-w-7xl"} flex-1 min-w-0`}>
        {tab === "overview" && <OverviewTab project={project} setup={setup} syncs={syncs} />}
        {tab === "syncs" && (
          <div className="space-y-6">
            {isAdmin && <FileUploader projectId={id} onUploaded={refreshProject} />}
            <SyncsList syncs={syncs} onDelete={onDeleteSync} onSetBaseline={onSetBaseline} onReparse={onReparse} onReparseAllEmpty={onReparseAllEmpty} onRename={onRenameSync} incompleteStats={incompleteStats} isAdmin={isAdmin} />
          </div>
        )}
        {tab === "gantt" && (
          activeSyncId ? (
            <GanttView
              key={activeSyncId}
              initialSelection={ganttSelection}
              initialTasks={ganttInitialTasks}
              syncMeta={ganttSyncMeta}
              projects={ganttProjects}
              availableColumnsCatalog={activeSync?.available_columns || []}
              projectName={project.name}
              canEditCompany={isAdmin}
            />
          ) : <EmptyHint t={t} />
        )}
        {tab === "kanban" && (activeSyncId ? <KanbanBoard data={kanbanData} /> : <EmptyHint t={t} />)}
        {tab === "lob" && (activeSyncId ? <LineOfBalance data={lobData} /> : <EmptyHint t={t} />)}
        {tab === "compare" && <SyncComparator syncs={syncs} />}
        {tab === "restrictions" && <RestrictionsTable projectId={id} />}
        {tab === "sharepoint" && <SharePointTab projectId={id} onImported={refreshProject} />}
        {tab === "settings" && <ProjectSettingsTab project={project} onUpdated={refreshProject} />}
      </div>

      <AIAnalysisPanel syncId={activeSyncId} open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}

function EmptyHint({ t }) {
  return <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center bg-white"><p className="text-sm text-zinc-500">{t("pick_a_sync")}</p></div>;
}

function OverviewTab({ project, setup, syncs }) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="overview-tab">
      <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6">
        <div className="cs-label">{t("setup_progress")}</div>
        <div className="flex items-baseline gap-3 mt-2">
          <span className="font-mono text-4xl font-semibold">{setup?.completed_count || 0}</span>
          <span className="text-zinc-500">{t("of")} {setup?.total || 0} {t("completed")}</span>
        </div>
        <div className="mt-3 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-cs-accent transition-all" style={{ width: `${((setup?.completed_count || 0) / (setup?.total || 1)) * 100}%` }} />
        </div>
        <ul className="mt-6 space-y-2">
          {setup?.steps?.map((s) => (
            <li key={s.id} className="flex items-center justify-between border-t border-zinc-100 pt-2">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${s.complete ? "bg-cs-success" : "bg-zinc-300"}`} />
                <span className="text-sm">{s.name}</span>
              </div>
              <span className="font-mono text-xs text-zinc-500">{s.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <div className="cs-label">{t("description")}</div>
        <p className="text-sm text-zinc-700 mt-2">{project.description || "—"}</p>
        <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
          <div><div className="cs-label !text-[10px]">{t("start_date")}</div><div className="font-mono mt-1">{(project.start_date || "—").toString().slice(0, 10)}</div></div>
          <div><div className="cs-label !text-[10px]">{t("end_date")}</div><div className="font-mono mt-1">{(project.end_date || "—").toString().slice(0, 10)}</div></div>
          <div><div className="cs-label !text-[10px]">{t("syncs")}</div><div className="font-mono mt-1">{syncs.length}</div></div>
        </div>
      </div>
    </div>
  );
}

/**
 * useIsNarrow — media-query hook. Drives a real layout swap (table ⇄ cards)
 * instead of CSS `hidden` classes so only ONE copy of each row lives in the
 * DOM (duplicate data-testids break e2e selectors).
 */
function useIsNarrow(query = "(max-width: 767px)") {
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}

function SyncsList({ syncs, onDelete, onSetBaseline, onReparse, onReparseAllEmpty, onRename, incompleteStats, isAdmin }) {
  const { t } = useI18n();
  const isNarrow = useIsNarrow();
  if (!syncs.length) {
    return <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center bg-white" data-testid="syncs-empty"><p className="text-sm text-zinc-500">{t("no_syncs")}</p></div>;
  }
  const stats = incompleteStats || { incomplete_count: 0 };
  return (
    <div className="space-y-3" data-testid="syncs-list">
      {stats.incomplete_count > 0 && isAdmin && (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-4 py-3" data-testid="reparse-all-banner">
          <div className="text-xs text-amber-900">
            <div>
              <span className="font-semibold">{stats.incomplete_count}</span> {stats.incomplete_count !== 1 ? t("incomplete_versions_plural") : t("incomplete_versions_singular")}
            </div>
            <div className="mt-1 text-amber-800 space-y-0.5">
              {stats.empty_count > 0 && <div>· {stats.empty_count} {t("incomplete_breakdown_empty")}</div>}
              {stats.cf_regression_count > 0 && <div>· {stats.cf_regression_count} {t("incomplete_breakdown_cf_regression")}</div>}
              {stats.cf_missing_aliases_count > 0 && <div>· {stats.cf_missing_aliases_count} {t("incomplete_breakdown_missing_aliases")}</div>}
            </div>
          </div>
          <button onClick={onReparseAllEmpty} className="inline-flex items-center justify-center gap-1.5 bg-cs-blueprint text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-blue-700 shrink-0 sm:ml-3" data-testid="reparse-all-btn">
            <RefreshCw size={12} /> {t("reparse_all_incomplete_btn")}
          </button>
        </div>
      )}
      {isNarrow ? (
        <div className="space-y-2" data-testid="syncs-cards">
          {syncs.map((s) => (
            <SyncCard key={s.id} s={s} onRename={onRename} onReparse={onReparse} onSetBaseline={onSetBaseline} onDelete={onDelete} isAdmin={isAdmin} />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md">
          {/* overflow-x-auto (not hidden): long file names used to push the
              actions column outside the card, making delete/baseline
              unreachable on 1366px laptops. */}
          <div className="overflow-x-auto scrollbar-thin rounded-md">
            <table className="w-full min-w-[820px] table-fixed text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="cs-label px-4 py-3 w-14">v</th>
                  <th className="cs-label px-4 py-3">{t("title")}</th>
                  <th className="cs-label px-4 py-3 font-mono w-20">{t("tasks")}</th>
                  <th className="cs-label px-4 py-3 w-28">{t("start_date")}</th>
                  <th className="cs-label px-4 py-3 w-28">{t("end_date")}</th>
                  <th className="cs-label px-4 py-3 w-48 hidden 2xl:table-cell">{t("uploaded_by")}</th>
                  {isAdmin && <th className="px-3 py-3 w-[116px] sticky right-0 bg-zinc-50 border-l border-zinc-200" />}
                </tr>
              </thead>
              <tbody>
                {syncs.map((s) => (
                  <SyncRow key={s.id} s={s} onRename={onRename} onReparse={onReparse} onSetBaseline={onSetBaseline} onDelete={onDelete} isAdmin={isAdmin} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SyncNameEditor — inline-editable version name.
 *
 * Click on the name (or the pencil icon) to enter edit mode → a small input
 * replaces the label. Enter saves (calls `onRename`), Esc cancels. On touch
 * layouts the pencil is always visible (no hover to reveal it).
 */
function SyncNameEditor({ s, onRename, isAdmin, alwaysShowPencil = false }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.name || "");

  const startEdit = () => {
    if (!isAdmin) return;
    setDraft(s.name || "");
    setEditing(true);
  };
  const commit = async () => {
    const next = draft.trim();
    if (!next || next === s.name) { setEditing(false); return; }
    await onRename(s.id, next);
    setEditing(false);
  };
  const cancel = () => { setDraft(s.name || ""); setEditing(false); };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") cancel();
        }}
        className="w-full px-2 py-0.5 border border-cs-blueprint rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cs-blueprint/30"
        data-testid={`sync-name-input-${s.id}`}
      />
    );
  }
  return (
    <div className="flex items-center gap-1.5 min-w-0 group">
      {s.is_baseline && <Star size={12} className="text-cs-accent fill-cs-accent shrink-0" />}
      <span className={`font-medium text-zinc-900 min-w-0 ${alwaysShowPencil ? "break-words" : "truncate"}`} title={s.name}>{s.name}</span>
      {isAdmin && (
        <button
          onClick={startEdit}
          className={`${alwaysShowPencil ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity text-zinc-400 hover:text-cs-blueprint p-0.5 shrink-0`}
          title={t("sync_rename_tooltip")}
          data-testid={`sync-rename-btn-${s.id}`}
        >
          <Pencil size={11} />
        </button>
      )}
    </div>
  );
}

function SyncFileName({ s }) {
  const { t } = useI18n();
  if (!s.file_name) return null;
  return (
    <div className="text-xs text-zinc-500 font-mono flex items-center gap-1.5 min-w-0">
      <span className="truncate" title={s.file_name}>{s.file_name}</span>
      {s.sharepoint_web_url && (
        <a
          href={s.sharepoint_web_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-cs-blueprint hover:text-blue-700 shrink-0"
          title={t("sync_sharepoint_link_tooltip")}
          data-testid={`sync-sharepoint-link-${s.id}`}
        >
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function SyncActions({ s, onReparse, onSetBaseline, onDelete }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex items-center gap-0.5 shrink-0">
      <button
        onClick={() => onReparse(s.id)}
        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500 hover:text-cs-blueprint"
        title={t("sync_reparse_tooltip")}
        data-testid={`sync-reparse-${s.id}`}
      >
        <RefreshCw size={14} />
      </button>
      {!s.is_baseline && (
        <button onClick={() => onSetBaseline(s.id)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500 hover:text-cs-accent" title={t("set_baseline")} data-testid={`sync-baseline-${s.id}`}>
          <Star size={14} />
        </button>
      )}
      <button onClick={() => onDelete(s.id)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500 hover:text-red-600" title={t("delete")} data-testid={`sync-delete-${s.id}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function SyncRow({ s, onRename, onReparse, onSetBaseline, onDelete, isAdmin }) {
  return (
    <tr className="border-t border-zinc-100 group/row hover:bg-zinc-50/60" data-testid={`sync-row-${s.id}`}>
      <td className="px-4 py-3 font-mono text-zinc-900 font-semibold align-top">v{s.version}</td>
      <td className="px-4 py-3 align-top max-w-0">
        <SyncNameEditor s={s} onRename={onRename} isAdmin={isAdmin} />
        <SyncFileName s={s} />
        {s.status === "empty" && s.warnings?.length > 0 && (
          <div className="text-[10px] text-amber-700 mt-1 truncate" title={s.warnings[0]}>⚠ {s.warnings[0]}</div>
        )}
      </td>
      <td className="px-4 py-3 font-mono align-top">{s.task_count}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-700 align-top whitespace-nowrap">{(s.project_start || "—").toString().slice(0, 10)}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-700 align-top whitespace-nowrap">{(s.project_finish || "—").toString().slice(0, 10)}</td>
      <td className="px-4 py-3 text-xs text-zinc-700 align-top hidden 2xl:table-cell max-w-0">
        <div className="truncate" title={s.created_by_email}>{s.created_by_email}</div>
      </td>
      {isAdmin && (
        <td className="px-3 py-3 text-right align-top sticky right-0 bg-white group-hover/row:bg-zinc-50 border-l border-zinc-200">
          <SyncActions s={s} onReparse={onReparse} onSetBaseline={onSetBaseline} onDelete={onDelete} />
        </td>
      )}
    </tr>
  );
}

/** Stacked card layout for phones — every action stays reachable. */
function SyncCard({ s, onRename, onReparse, onSetBaseline, onDelete, isAdmin }) {
  const { t } = useI18n();
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`sync-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="inline-block font-mono text-[11px] font-semibold bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">v{s.version}</span>
          <div className="mt-1.5">
            <SyncNameEditor s={s} onRename={onRename} isAdmin={isAdmin} alwaysShowPencil />
          </div>
          <SyncFileName s={s} />
        </div>
        {isAdmin && <SyncActions s={s} onReparse={onReparse} onSetBaseline={onSetBaseline} onDelete={onDelete} />}
      </div>
      {s.status === "empty" && s.warnings?.length > 0 && (
        <div className="text-[10px] text-amber-700 mt-2">⚠ {s.warnings[0]}</div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-zinc-100">
        <div>
          <div className="cs-label !text-[10px]">{t("tasks")}</div>
          <div className="font-mono text-xs mt-0.5">{s.task_count}</div>
        </div>
        <div>
          <div className="cs-label !text-[10px]">{t("start_date")}</div>
          <div className="font-mono text-xs mt-0.5">{(s.project_start || "—").toString().slice(0, 10)}</div>
        </div>
        <div>
          <div className="cs-label !text-[10px]">{t("end_date")}</div>
          <div className="font-mono text-xs mt-0.5">{(s.project_finish || "—").toString().slice(0, 10)}</div>
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mt-2 truncate">{s.created_by_email}</div>
    </div>
  );
}

function ProjectSettingsTab({ project, onUpdated }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [form, setForm] = useState({ name: project.name || "", description: project.description || "", client_name: project.client_name || "", location: project.location || "", start_date: (project.start_date || "").slice(0, 10), end_date: (project.end_date || "").slice(0, 10), color: project.color || "#F97316" });
  const [saving, setSaving] = useState(false);

  const onSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    setSaving(true);
    try {
      const { patchProject } = await import("../lib/api");
      const payload = { ...form };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      await patchProject(project.id, payload);
      toast.success(t("success"));
      onUpdated?.();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={onSave} className="bg-white border border-zinc-200 rounded-md p-6 max-w-2xl space-y-4" data-testid="project-settings-tab">
      <h3 className="font-heading text-lg font-semibold">{t("project_settings")}</h3>
      <div>
        <label className="cs-label block mb-1.5">{t("project_name")}</label>
        <input type="text" required value={form.name} disabled={!isAdmin} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-100" data-testid="settings-name-input" />
      </div>
      <div>
        <label className="cs-label block mb-1.5">{t("description")}</label>
        <textarea rows={3} value={form.description} disabled={!isAdmin} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-100" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="cs-label block mb-1.5">{t("client")}</label>
          <input type="text" value={form.client_name} disabled={!isAdmin} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-100" />
        </div>
        <div>
          <label className="cs-label block mb-1.5">{t("location")}</label>
          <input type="text" value={form.location} disabled={!isAdmin} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm disabled:bg-zinc-100" />
        </div>
        <div>
          <label className="cs-label block mb-1.5">{t("start_date")}</label>
          <input type="date" value={form.start_date} disabled={!isAdmin} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono disabled:bg-zinc-100" />
        </div>
        <div>
          <label className="cs-label block mb-1.5">{t("end_date")}</label>
          <input type="date" value={form.end_date} disabled={!isAdmin} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono disabled:bg-zinc-100" />
        </div>
        <div>
          <label className="cs-label block mb-1.5">{t("color")}</label>
          <input type="color" value={form.color} disabled={!isAdmin} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-9 border border-zinc-300 rounded-md disabled:opacity-50" />
        </div>
      </div>
      {isAdmin && (
        <button type="submit" disabled={saving} className="px-4 py-2 bg-cs-accent text-white rounded-md text-sm font-semibold hover:bg-cs-accentHover disabled:opacity-60" data-testid="settings-save-btn">
          {saving ? "..." : t("save")}
        </button>
      )}
    </form>
  );
}
